import type { Category } from '../types';
import { FACTS } from '../facts';

const L = FACTS.complaintsLadder;

/**
 * "Reporting a problem" — GL Plus's own operational guidance (work half).
 * Ladder numbers come from FACTS so a policy change is one edit.
 */
export const reportingAProblem: Category = {
  slug: "reporting-a-problem",
  half: "work",
  accent: '#E08A00',
  icon: "dollar",
  label: { en: "Reporting a problem", es: "Reportar un problema" },
  blurb: {
    en: "Customer or vendor issue? How to report it, what happens next, and what never counts against you.",
    es: "¿Un problema con un cliente o un vendedor? Cómo reportarlo, qué pasa después y qué nunca cuenta en tu contra.",
  },
  guides: [
    {
      slug: "customer-issue",
      related: [
        "reporting-a-problem/what-happens-next",
        "reporting-a-problem/what-never-counts-against-you",
        "safety-on-deliveries/night-deliveries",
        "safety-on-deliveries/dogs-and-animals",
      ],
      body: {
        en: {
          title: "Reporting a customer issue",
          summary:
            "How to file a report when something goes wrong with a customer, and how to make sure you are never matched with them again.",
          sections: [
            {
              heading: "When to file a report",
              paragraphs: [
                "Not every hiccup needs a report. Some things do. A customer who is rude or threatening. A delivery you could not complete because nobody was home. An address that turned out to be wrong. Anything that made you feel unsafe.",
                "If you are not sure, file it. A short report costs you a minute and gives you a record.",
              ],
            },
            {
              heading: "How to file it",
              bullets: [
                "Open the run in the Runner app and tap Report a problem. You can also file from the portal after the run.",
                "Pick the category that fits best, then describe what happened in a few plain sentences.",
                "Add photos if you have them: the door, the bags, the address sign.",
                "File the same day, while the details are fresh.",
              ],
            },
            {
              heading: "Blocking a customer",
              paragraphs: [
                "If you never want to see a customer again, block them. You can do it from the report or from your run history. Once blocked, that customer is never matched to you again.",
                "Blocking is your call. You do not need a reason, and nobody will ask for one.",
              ],
            },
            {
              heading: "Safety comes first",
              paragraphs: [
                "If you are in danger, call 911 first, always. Then step away. Safety concerns are handled by Grocery List Plus, never confronted at a door. File the report once you are somewhere safe.",
              ],
            },
          ],
          actions: [
            "Find the Report a problem button on a run screen now, before you need it.",
            "Take a photo at every drop-off. It is your record if a question comes up later.",
            "Open your block list in the portal and make sure it matches who you do not want to serve.",
          ],
        },
        es: {
          title: "Reportar un problema con un cliente",
          summary:
            "Cómo presentar un reporte cuando algo sale mal con un cliente, y cómo asegurarte de que nunca te vuelvan a asignar a esa persona.",
          sections: [
            {
              heading: "Cuándo presentar un reporte",
              paragraphs: [
                "No todo contratiempo necesita un reporte. Algunas cosas sí. Un cliente grosero o amenazante. Una entrega que no pudiste completar porque no había nadie en casa. Una dirección que resultó ser incorrecta. Cualquier cosa que te hiciera sentir inseguro.",
                "Si tienes dudas, repórtalo. Un reporte breve te cuesta un minuto. No hacerlo puede costarte el registro que necesites después.",
              ],
            },
            {
              heading: "Cómo presentarlo",
              bullets: [
                "Abre el recorrido en la app de Runner y toca Reportar un problema. También puedes hacerlo desde el portal después del recorrido.",
                "Elige la categoría que mejor corresponda y describe lo que pasó en pocas frases sencillas.",
                "Agrega fotos si las tienes: la puerta, las bolsas, el letrero con la dirección.",
                "Repórtalo el mismo día, mientras los detalles están frescos.",
              ],
            },
            {
              heading: "Bloquear a un cliente",
              paragraphs: [
                "Si no quieres volver a ver a un cliente, bloquéalo. Puedes hacerlo desde el reporte o desde tu historial de recorridos. Una vez bloqueado, ese cliente nunca vuelve a ser asignado a ti.",
                "Bloquear es tu decisión. No necesitas dar una razón y nadie te la pedirá.",
              ],
            },
            {
              heading: "La seguridad va primero",
              paragraphs: [
                "Si estás en peligro, llama al 911 primero, siempre. Luego aléjate. Los asuntos de seguridad los maneja Lista de Compras Plus; nunca se confrontan en una puerta. Presenta el reporte cuando estés en un lugar seguro.",
              ],
            },
          ],
          actions: [
            "Ubica ahora el botón Reportar un problema en la pantalla de un recorrido, para saber dónde está antes de necesitarlo.",
            "Toma una foto en cada entrega. Es tu registro si después surge una pregunta.",
            "Abre tu lista de bloqueados en el portal y confirma que refleja a quién no quieres atender.",
          ],
        },
      },
    },
    {
      slug: "vendor-issue",
      related: [
        "reporting-a-problem/what-happens-next",
        "reporting-a-problem/what-never-counts-against-you",
        "reading-your-pay-statement/when-a-number-looks-wrong",
      ],
      body: {
        en: {
          title: "Reporting a vendor or store issue",
          summary:
            "What to do when a local vendor or chain store has a problem with your pickup, from a missing item to a card that will not run.",
          sections: [
            {
              heading: "Common pickup problems",
              bullets: [
                "An item is out of stock and there is no accepted substitute.",
                "The order is not ready when you arrive, or the vendor cannot find it.",
                "The Grocery List Plus virtual card is declined at a chain-store register.",
                "Staff will not release the order to you.",
                "Something in the order looks damaged or spoiled.",
              ],
            },
            {
              heading: "Out of stock and substitutions",
              paragraphs: [
                "Mark the item as out of stock in the app. If the customer allows substitutions, the app shows what they will accept. If they do not, do not guess. Skip the item.",
                "An out-of-stock exception marked in the app never counts against you.",
              ],
            },
            {
              heading: "The card will not run",
              paragraphs: [
                "At chain-store stops you pay with a Grocery List Plus-funded virtual card. It is never your own money. If the card is declined, do not pay out of pocket.",
                "Tap the card help option in the app. Most declines clear within a minute. If it still fails, mark the stop as a store exception and file a report.",
              ],
            },
            {
              heading: "Filing a vendor report",
              paragraphs: [
                "From the stop screen, tap Report a problem and pick the vendor category. Say what happened in plain words. Photos of the receipt, the empty shelf, or the damaged item help.",
                "Grocery List Plus follows up with the vendor. You do not need to.",
              ],
            },
          ],
          actions: [
            "Check the substitution setting on each order before you shop, so you know what to do when a shelf is empty.",
            "Walk through the card help flow once in the app, so you are not learning it at a register.",
            "Never pay out of pocket for a chain-store order. Mark the exception instead.",
          ],
        },
        es: {
          title: "Reportar un problema con un vendedor o una tienda",
          summary:
            "Qué hacer cuando un vendedor local o una tienda de cadena tiene un problema con tu recogida, desde un artículo faltante hasta una tarjeta que no pasa.",
          sections: [
            {
              heading: "Problemas comunes al recoger",
              bullets: [
                "Un artículo está agotado y no hay un sustituto aceptado.",
                "El pedido no está listo cuando llegas, o el vendedor no lo encuentra.",
                "La tarjeta virtual de Lista de Compras Plus es rechazada en la caja de una tienda de cadena.",
                "El personal no quiere entregarte el pedido.",
                "Algo en el pedido se ve dañado.",
              ],
            },
            {
              heading: "Agotados y sustituciones",
              paragraphs: [
                "Marca el artículo como agotado en la app. Si el cliente permite sustituciones, la app muestra qué acepta. Si no, no adivines: omite el artículo.",
                "Una excepción por artículo agotado marcada en la app nunca cuenta en tu contra.",
              ],
            },
            {
              heading: "La tarjeta no pasa",
              paragraphs: [
                "En las paradas de tienda de cadena pagas con una tarjeta virtual financiada por Lista de Compras Plus. Nunca es tu propio dinero. Si la tarjeta es rechazada, no pagues de tu bolsillo.",
                "Toca la opción de ayuda con la tarjeta en la app. La mayoría de los rechazos se resuelven en un minuto. Si sigue fallando, marca la parada como excepción de tienda y presenta un reporte.",
              ],
            },
            {
              heading: "Presentar un reporte de vendedor",
              paragraphs: [
                "Desde la pantalla de la parada, toca Reportar un problema y elige la categoría de vendedor. Cuenta lo que pasó con palabras sencillas. Las fotos del recibo, del estante vacío o del artículo dañado ayudan.",
                "Lista de Compras Plus da seguimiento con el vendedor; tú no.",
              ],
            },
          ],
          actions: [
            "Revisa la configuración de sustituciones de cada pedido antes de comprar.",
            "Practica una vez el flujo de ayuda con la tarjeta en la app, no en la caja.",
            "Nunca pagues de tu bolsillo un pedido de tienda de cadena. Marca la excepción.",
          ],
        },
      },
    },
    {
      slug: "what-happens-next",
      related: [
        "reporting-a-problem/customer-issue",
        "reporting-a-problem/what-never-counts-against-you",
        "if-you-have-an-accident/incident-checklist",
      ],
      body: {
        en: {
          title: "What happens after you file a report",
          summary:
            "Every report is read by a person at Grocery List Plus. Here is the timeline, what you will hear back, and what stays private.",
          sections: [
            {
              heading: "A person reads it",
              paragraphs: [
                "There are no automated decisions. Every report, whether yours, a customer's, or a vendor's, is reviewed by a human at Grocery List Plus.",
                "We aim to acknowledge your report within 1 business day and to reach a decision within 3 business days. Complicated cases can take longer. If yours does, we will tell you.",
              ],
            },
            {
              heading: "What we might ask you",
              bullets: [
                "A clarifying question about what happened.",
                "A photo or timestamp from the run.",
                "Whether you want the customer blocked, if you have not done it already.",
              ],
            },
            {
              heading: "What you will hear back",
              paragraphs: [
                "You get a message in the Runner app and in the portal when the review closes. It tells you the outcome as it affects you: whether an exception was confirmed, whether a payment was adjusted, whether a customer was blocked.",
                "If a number on your pay statement changed because of a report, the statement shows it on its own line.",
              ],
            },
            {
              heading: "What stays private",
              paragraphs: [
                "Grocery List Plus never discusses another Runner's or customer's case with you, and never discusses yours with them. You learn the outcome for you, not what happened to anyone else. That is the same protection everyone gets, including you.",
              ],
            },
          ],
          actions: [
            "Turn on notifications for the Runner app so you do not miss a follow-up question.",
            "If a report has been open more than 3 business days with no word, reply in the thread. A person will pick it up.",
            "Keep your own note of the date and run number for anything you report.",
          ],
        },
        es: {
          title: "Qué pasa después de presentar un reporte",
          summary:
            "Cada reporte lo lee una persona de Lista de Compras Plus. Aquí tienes los plazos, qué respuesta recibirás y qué se mantiene privado.",
          sections: [
            {
              heading: "Lo lee una persona",
              paragraphs: [
                "No hay decisiones automatizadas. Cada reporte, ya sea tuyo, de un cliente o de un vendedor, lo revisa una persona de Lista de Compras Plus.",
                "Nuestra meta es acusar recibo de tu reporte en 1 día hábil y tomar una decisión en 3 días hábiles. Los casos complicados pueden tardar más. Si el tuyo tarda, te lo diremos.",
              ],
            },
            {
              heading: "Qué podríamos preguntarte",
              bullets: [
                "Una aclaración sobre lo que pasó.",
                "Una foto o una hora del recorrido.",
                "Si quieres bloquear al cliente, en caso de que no lo hayas hecho ya.",
              ],
            },
            {
              heading: "Qué respuesta recibirás",
              paragraphs: [
                "Recibes un mensaje en la app de Runner y en el portal cuando se cierra la revisión. Te dice el resultado en lo que te afecta: si se confirmó una excepción, si se ajustó un pago, si se bloqueó a un cliente.",
                "Si una cifra de tu estado de pago cambió por un reporte, el estado lo muestra en su propia línea.",
              ],
            },
            {
              heading: "Qué se mantiene privado",
              paragraphs: [
                "Lista de Compras Plus nunca habla contigo del caso de otro Runner o de un cliente, y nunca habla del tuyo con ellos. Conoces el resultado que te corresponde, no lo que le pasó a alguien más. Es la misma protección que reciben todos, incluido tú.",
              ],
            },
          ],
          actions: [
            "Activa las notificaciones de la app de Runner para no perderte una pregunta de seguimiento.",
            "Si un reporte lleva más de 3 días hábiles abierto sin respuesta, escribe en el hilo. Una persona lo retomará.",
            "Guarda tu propia nota con la fecha y el número de recorrido de todo lo que reportes.",
          ],
        },
      },
    },
    {
      slug: "what-never-counts-against-you",
      related: [
        "building-your-book/your-schedule-is-a-promise",
        "reporting-a-problem/what-happens-next",
        "safety-on-deliveries/dogs-and-animals",
        "building-your-book/capacity-and-trusted-runner",
      ],
      body: {
        en: {
          title: "What never counts against you",
          summary:
            "Exceptions marked in the app are never held against you, and if a complaint is filed about you, the ladder is published so there are no surprises.",
          sections: [
            {
              heading: "Exceptions are not complaints",
              paragraphs: [
                "Some things are outside your control. When you mark them as an exception in the app, they never count against you:",
              ],
              bullets: [
                "Customer not home, after you have followed the drop-off steps.",
                "An unsafe situation: a loose dog, a threat, a dark or blocked entry.",
                "Store out of stock, with no accepted substitute.",
                "A store or vendor that cannot or will not release the order.",
              ],
            },
            {
              heading: "Mark it at the time",
              paragraphs: [
                "The key is marking the exception in the app when it happens. An unmarked incomplete delivery looks like a missed delivery. A marked one is a record that you did the right thing.",
              ],
            },
            {
              heading: "What does count",
              paragraphs: [
                "There is one thing on the other side of the line: a delivery you accepted that never happened, with no word to Grocery List Plus and none to the customer. You choose your own days, so a run on your schedule is a promise a household planned around.",
                "A real reason plus early notice is handled, every time. Silence is the version that ends a Runner relationship. See \"Your schedule is a promise.\"",
              ],
            },
            {
              heading: "The complaint ladder",
              paragraphs: ["If a customer files a complaint about you, it follows a published ladder. No hidden scores."],
              bullets: [
                L.reviewAt + " complaints in " + L.windowDays + " days: a human review.",
                L.suspendAt + " complaints in " + L.windowDays + " days: suspension pending review.",
                "One confirmed theft: immediate deactivation.",
              ],
            },
            {
              heading: "You get to respond",
              paragraphs: [
                "A single complaint on its own does nothing to your standing. If a complaint reaches review, you are told what was alleged and you can respond in your own words before any decision. A person at Grocery List Plus weighs both sides. Exceptions you marked at the time are part of your side.",
                "Grocery List Plus never decides by algorithm, never discusses your case with a customer or another Runner, and never asks you to confront anyone.",
              ],
            },
          ],
          actions: [
            "Mark every exception in the app the moment it happens, not after the run.",
            "Check your standing in the portal so you know where you are on the ladder. Most Runners are at zero.",
            "If you get a complaint notice, respond within the window. Your side matters.",
          ],
        },
        es: {
          title: "Qué nunca cuenta en tu contra",
          summary:
            "Las excepciones marcadas en la app nunca se usan en tu contra, y si alguien presenta una queja sobre ti, la escalera está publicada para que no haya sorpresas.",
          sections: [
            {
              heading: "Las excepciones no son quejas",
              paragraphs: [
                "Algunas cosas están fuera de tu control. Cuando las marcas como excepción en la app, nunca cuentan en tu contra:",
              ],
              bullets: [
                "Cliente ausente, después de seguir los pasos de entrega.",
                "Una situación insegura: un perro suelto, una amenaza, una entrada oscura o bloqueada.",
                "Tienda sin existencias, sin un sustituto aceptado.",
                "Una tienda o un vendedor que no puede o no quiere entregar el pedido.",
              ],
            },
            {
              heading: "Márcala en el momento",
              paragraphs: [
                "La clave es marcar la excepción en la app cuando ocurre. Una entrega incompleta sin marcar parece una entrega perdida. Una marcada es un registro de que hiciste lo correcto.",
              ],
            },
            {
              heading: "Qué sí cuenta",
              paragraphs: [
                "Hay una sola cosa del otro lado de la línea: una entrega que aceptaste y que nunca ocurrió, sin avisarle a Lista de Compras Plus ni al cliente. Tú eliges tus propios días, así que un recorrido en tu horario es una promesa alrededor de la cual un hogar hizo planes.",
                "Una razón real más un aviso temprano siempre se resuelve. El silencio es la versión que termina una relación con un Runner. Lee \"Tu horario es una promesa\".",
              ],
            },
            {
              heading: "La escalera de quejas",
              paragraphs: [
                "Si un cliente presenta una queja sobre ti, sigue una escalera publicada. No hay puntajes ocultos.",
              ],
              bullets: [
                L.reviewAt + " quejas en " + L.windowDays + " días: revisión por una persona.",
                L.suspendAt + " quejas en " + L.windowDays + " días: suspensión en espera de revisión.",
                "Un robo confirmado: desactivación inmediata.",
              ],
            },
            {
              heading: "Puedes responder",
              paragraphs: [
                "Una sola queja no cambia tu situación. Si una queja llega a revisión, se te informa qué se alegó y puedes responder con tus propias palabras antes de cualquier decisión. Una persona de Lista de Compras Plus considera ambas versiones. Las excepciones que marcaste en el momento forman parte de tu versión.",
                "Lista de Compras Plus nunca decide por algoritmo, nunca habla de tu caso con un cliente ni con otro Runner, y nunca te pide confrontar a nadie.",
              ],
            },
          ],
          actions: [
            "Marca cada excepción en la app en el momento en que ocurre, no después del recorrido.",
            "Revisa tu situación en el portal. La mayoría de los Runners están en cero.",
            "Si recibes un aviso de queja, responde dentro del plazo. Tu versión importa.",
          ],
        },
      },
    },
  ],
};
